import { useReferralLink, useReferralStats } from '@/hooks/useReferrals';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Copy, Share2, Users, Trophy, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';

export function ReferralCard() {
  const { code, referralLink } = useReferralLink();
  const { data: stats, isLoading } = useReferralStats();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!referralLink) return;
    
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (!referralLink) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Decision Sales OS',
          text: 'Check out Decision Sales OS - the sales OS that helps you close more deals!',
          url: referralLink,
        });
      } catch (error) {
        // User cancelled or error
        console.error('Share failed', error);
      }
    } else {
      // Fallback to copy
      handleCopy();
    }
  };

  if (!code) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">Loading referral code...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Referral Program</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Referral Code */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Your Referral Code</label>
          <div className="flex gap-2">
            <Input
              value={code}
              readOnly
              className="font-mono font-semibold"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopy}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Referral Link */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Referral Link</label>
          <div className="flex gap-2">
            <Input
              value={referralLink || ''}
              readOnly
              className="text-xs"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopy}
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats */}
        {!isLoading && stats && (
          <div className="grid grid-cols-3 gap-4 pt-4 border-t">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                <Users className="w-4 h-4" />
              </div>
              <p className="text-2xl font-bold">{stats.total_referrals}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                <Trophy className="w-4 h-4" />
              </div>
              <p className="text-2xl font-bold">{stats.converted}</p>
              <p className="text-xs text-muted-foreground">Converted</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                <Clock className="w-4 h-4" />
              </div>
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
          </div>
        )}

        {/* Reward Info */}
        <div className="p-4 rounded-lg bg-muted/50">
          <p className="text-sm font-medium mb-1">Reward</p>
          <p className="text-xs text-muted-foreground">
            Get 1 month Pro or 7 days Pro for each paying referral. Tag @decisionsalesos to unlock rewards.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

